// src/utils/cacheBuster.js
// Automatically detects new deploys and refreshes user's browser
// User does NOT need to clear cache/cookies/history

const STORAGE_KEY = 'app_deployed_version';
const CHECK_INTERVAL = 60000; // Check every 60 seconds
const INITIAL_DELAY = 3000;   // First check after 3 seconds

let isRefreshing = false;

/**
 * Fetch the current server version
 */
async function fetchServerVersion() {
  try {
    const res = await fetch(`/backend/api/version?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' }
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.version;
  } catch (e) {
    return null;
  }
}

/**
 * Get stored version from localStorage
 */
function getStoredVersion() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return null;
  }
}

/**
 * Force reload with cache bypass
 */
async function forceReload() {
  if (isRefreshing) return;
  isRefreshing = true;

  console.log('🔄 New version detected — refreshing app...');

  try {
    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of registrations) {
        await reg.unregister();
        console.log('✅ Service worker unregistered');
      }
    }

    // Clear all caches (Cache Storage API)
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => {
        console.log('🗑️ Deleting cache:', name);
        return caches.delete(name);
      }));
    }

    // Delete HTTP cache by re-fetching index.html with cache-bypass
    await fetch(`/?_cb=${Date.now()}`, {
      cache: 'reload',
      headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' }
    });
  } catch (err) {
    console.warn('Cache clear error (continuing to reload):', err);
  }

  // Force hard reload bypassing browser cache
  window.location.reload(true);
}

/**
 * Check for version changes
 */
async function checkVersion() {
  const serverVersion = await fetchServerVersion();
  if (!serverVersion) return;

  const storedVersion = getStoredVersion();

  if (!storedVersion) {
    // First visit — just store it
    localStorage.setItem(STORAGE_KEY, serverVersion);
    console.log(`✅ App version initialized: ${serverVersion}`);
    return;
  }

  if (storedVersion !== serverVersion) {
    console.log(`🆕 Version changed: ${storedVersion} → ${serverVersion}`);
    localStorage.setItem(STORAGE_KEY, serverVersion);
    await forceReload();
  }
}

/**
 * Start watching for version changes
 */
export function startCacheWatcher() {
  // Initial check
  setTimeout(checkVersion, INITIAL_DELAY);

  // Periodic check
  setInterval(checkVersion, CHECK_INTERVAL);

  // Check when user returns to tab
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkVersion();
    }
  });

  // Check when network comes back
  window.addEventListener('online', checkVersion);

  console.log('👀 Cache watcher started');
}

/**
 * Manually force an update (for "Check for updates" button)
 */
export async function forceUpdate() {
  await forceReload();
}

export default { startCacheWatcher, forceUpdate };